import type { ColumnDef } from '@tanstack/vue-table';
import { computed, h } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import CapacityAllocationBar from '@/components/capacity/CapacityAllocationBar.vue';
import CapacityTypePill from '@/components/capacity/CapacityTypePill.vue';
import { useScheduleGridMetrics } from '@/components/scheduling/scheduleGridMetrics';
import ScheduleNoPlanCell from '@/components/scheduling/ScheduleNoPlanCell.vue';
import ScheduleWeekCell from '@/components/scheduling/ScheduleWeekCell.vue';
import ScheduleWeekHeader from '@/components/scheduling/ScheduleWeekHeader.vue';
import ToScheduleQueueCell from '@/components/scheduling/ToScheduleQueueCell.vue';
import ToScheduleQueueHeader from '@/components/scheduling/ToScheduleQueueHeader.vue';
import ToScheduleQueueTypeCell from '@/components/scheduling/ToScheduleQueueTypeCell.vue';
import type { NoPlanMenu } from '@/composables/useNoPlanMenu';
import { useScheduleBoardDragDrop } from '@/composables/useScheduleBoardDragDrop';
import type { SelectedPo } from '@/composables/useSelectedPo';
import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
import type { PurchaseOrder } from '@/types/purchaseOrders';
import { isPastWeekKey } from '@/utils/calendarBuckets';
import type { CalendarBucket } from '@/utils/calendarBuckets';
import { cellOrderKeys } from '@/utils/scheduleBoard';
import type { BoardRow, ScheduleGroup, VendorLane } from '@/utils/scheduleBoard';
import { queueTotals } from '@/utils/toScheduleQueue';

// A hard height for the Scheduled row rather than one that grows with its blocks: only the
// weeks near the viewport are mounted, so a content-sized row would resize as the tallest
// week in the DOM changed under a scroll. Overflow scrolls inside the cell instead. The grid
// sizes it to whatever the capacity rows leave on screen — a custom property because this
// vnode renders through FlexRender and so carries no component's scope id.
const SCHEDULED_CELL_CLASS = 'h-[var(--schedule-row-height)]';
const VENDOR_LABEL_CLASS = 'text-sm font-bold text-blue-700';

export interface ScheduleBoardColumnsOptions {
  buckets: Ref<CalendarBucket[]> | ComputedRef<CalendarBucket[]>;
  selectedLane: ComputedRef<VendorLane | null>;
  canWrite: ComputedRef<boolean>;
  // write:capacity, distinct from canWrite (write:po): planning a week is a capacity edit.
  canWriteCapacity: ComputedRef<boolean>;
  canViewActivity: ComputedRef<boolean>;
  canOpenReport: ComputedRef<boolean>;
  selectedPo: SelectedPo;
  noPlanMenu: NoPlanMenu;
  onSelectGroup: (weekKey: string, group: ScheduleGroup) => void;
  onViewNote: (lane: VendorLane, weekKey: string, group: ScheduleGroup) => void;
  onEditCapacity: (lane: VendorLane, weekKey: string, group: ScheduleGroup) => void;
  onViewLineItems: (lane: VendorLane, group: ScheduleGroup) => void;
  onRemove: (lane: VendorLane, weekKey: string, group: ScheduleGroup) => void;
  onDragStart: (lane: VendorLane, weekKey: string, group: ScheduleGroup) => void;
  onDrop: (vendorCode: string, weekKey: string) => void;
  onScheduleWeek: (weekKey: string) => void;
  onOpenReport: (weekKey: string) => void;
  queue: {
    items: ComputedRef<PurchaseOrder[]>;
    loading: ComputedRef<boolean>;
    initialLoading: ComputedRef<boolean>;
    error: ComputedRef<string | null>;
    syncedAt: ComputedRef<string | null>;
    syncError: ComputedRef<string | null>;
    refreshError: ComputedRef<string | null>;
    hidden: ComputedRef<boolean>;
  };
  onToggleQueue: () => void;
  onRefreshQueue: () => void;
  onScheduleFromQueue: (poNumber: string) => void;
  onQueueDragStart: (poNumber: string) => void;
}

// The board's grid layout: a pinned vendor/type column, then one column per week. Header and
// cell render functions deliberately read reactive state when they run rather than closing
// over a snapshot — FlexRender tracks them, so permissions and selection changes re-render
// without rebuilding every column definition.
export function useScheduleBoardColumns(options: ScheduleBoardColumnsOptions): ComputedRef<ColumnDef<BoardRow>[]> {
  const store = useCapacityCalendarStore();
  const dnd = useScheduleBoardDragDrop();
  const { vendorColumnWidth, queueColumnWidth, weekColumnWidth } = useScheduleGridMetrics();
  // Computed once and shared by every type-row cell rather than re-summing the whole queue per row.
  const totals = computed(() => queueTotals(options.queue.items.value));

  // Hidden on weeks the vendor has no plan for — scheduling into those has nowhere to go —
  // and on weeks that have ended, which the dialog's week list no longer offers either.
  function canScheduleWeek(weekKey: string): boolean {
    if (!options.canWrite.value || isPastWeekKey(weekKey)) return false;
    return Boolean(options.selectedLane.value?.cells[weekKey]?.hasPlan);
  }

  // The report splits scheduled quantity by customer, so a week with nothing scheduled has
  // nothing to split — it would open on a full ring of unused capacity.
  function canReportOnWeek(weekKey: string): boolean {
    return options.canOpenReport.value && (options.selectedLane.value?.cells[weekKey]?.scheduledTotal ?? 0) > 0;
  }

  return computed<ColumnDef<BoardRow>[]>(() => [
    {
      id: 'vendor',
      header: '',
      size: vendorColumnWidth.value,
      cell: ({ row }) =>
        row.original.kind === 'scheduled'
          ? h('div', { class: 'flex flex-col gap-1' }, [
              h('span', { class: VENDOR_LABEL_CLASS }, 'Scheduled'),
              h('span', { class: 'text-[10px] text-gray-400' }, 'POs and reservations'),
            ])
          : h(CapacityTypePill, { category: row.original.calendarRow.category, label: row.original.calendarRow.typeLabel }),
    },
    {
      id: 'queue',
      header: () =>
        h(ToScheduleQueueHeader, {
          count: options.queue.items.value.length,
          hidden: options.queue.hidden.value,
          loading: options.queue.loading.value,
          onToggle: () => options.onToggleQueue(),
        }),
      size: queueColumnWidth.value,
      cell: ({ row }) => {
        if (row.original.kind === 'scheduled') {
          return h('div', { class: SCHEDULED_CELL_CLASS }, [
            h(ToScheduleQueueCell, {
              items: options.queue.items.value,
              loading: options.queue.loading.value,
              initialLoading: options.queue.initialLoading.value,
              error: options.queue.error.value,
              syncedAt: options.queue.syncedAt.value,
              syncError: options.queue.syncError.value,
              refreshError: options.queue.refreshError.value,
              hidden: options.queue.hidden.value,
              canWrite: options.canWrite.value,
              vendorCode: options.selectedLane.value?.vendorCode ?? '',
              onSchedule: (poNumber: string) => options.onScheduleFromQueue(poNumber),
              onRefresh: () => options.onRefreshQueue(),
              onDragStart: (poNumber: string) => options.onQueueDragStart(poNumber),
              onDragEnd: () => dnd.cancelDrag(),
            }),
          ]);
        }
        return h(ToScheduleQueueTypeCell, {
          total: totals.value.byType[row.original.calendarRow.typeCode] ?? 0,
          unknownCount: totals.value.unknownCount,
          hidden: options.queue.hidden.value,
        });
      },
    },
    ...options.buckets.value.map<ColumnDef<BoardRow>>((bucket) => ({
      id: bucket.key,
      header: () =>
        h(ScheduleWeekHeader, {
          label: bucket.label,
          isCurrent: bucket.isCurrent,
          canSchedule: canScheduleWeek(bucket.key),
          canOpenReport: canReportOnWeek(bucket.key),
          onSchedule: () => options.onScheduleWeek(bucket.key),
          onOpenReport: () => options.onOpenReport(bucket.key),
        }),
      size: weekColumnWidth.value,
      cell: ({ row }) => {
        if (row.original.kind === 'scheduled') {
          const lane = row.original.lane;
          return h('div', { class: SCHEDULED_CELL_CLASS }, [
            h(ScheduleWeekCell, {
              cell: lane.cells[bucket.key],
              canWrite: options.canWrite.value,
              canViewActivity: options.canViewActivity.value,
              selectedPoNumber: options.selectedPo.selectedPoNumber.value,
              selectedBlockKey: options.selectedPo.selectedBlock.value?.blockKey ?? null,
              weekKey: bucket.key,
              vendorId: lane.vendorId,
              dropEligible: dnd.isDragging.value && dnd.isCompatibleTarget({ vendorCode: lane.vendorCode, weekKey: bucket.key }),
              onSelectGroup: (group: ScheduleGroup) => options.onSelectGroup(bucket.key, group),
              onViewNote: (group: ScheduleGroup) => options.onViewNote(lane, bucket.key, group),
              onEditCapacity: (group: ScheduleGroup) => options.onEditCapacity(lane, bucket.key, group),
              onViewLineItems: (group: ScheduleGroup) => options.onViewLineItems(lane, group),
              onRemove: (group: ScheduleGroup) => options.onRemove(lane, bucket.key, group),
              onDragStart: (group: ScheduleGroup) => options.onDragStart(lane, bucket.key, group),
              onDragEnd: () => dnd.cancelDrag(),
              onDrop: () => options.onDrop(lane.vendorCode, bucket.key),
            }),
          ]);
        }
        const calendarRow = row.original.calendarRow;
        const allocation = store.allocations[calendarRow.id]?.[bucket.key];
        if (!allocation) {
          const menuOpen = options.noPlanMenu.isMenuCell(calendarRow.id, bucket.key);
          return h(ScheduleNoPlanCell, {
            editable: options.canWriteCapacity.value,
            menuOpen,
            unit: calendarRow.unit,
            previousWeekPlan: menuOpen ? options.noPlanMenu.previousWeekPlan(calendarRow.id, bucket.key) : null,
            onOpen: () => options.noPlanMenu.openMenu(calendarRow.id, bucket.key),
            onClose: () => options.noPlanMenu.closeMenu(calendarRow.id, bucket.key),
            onCopy: () => void options.noPlanMenu.copyPreviousWeek(calendarRow.id, bucket.key),
            onAdd: (capacity: number) => void options.noPlanMenu.addCapacity(calendarRow.id, bucket.key, capacity),
          });
        }
        // Read-only bar: no rowId/vendorId/capacityTypeId means drag stays disabled here —
        // an existing plan is edited in the Capacity Calendar, not on this page.
        return h('div', { class: 'px-1' }, [
          h(CapacityAllocationBar, {
            purchaseOrders: allocation.purchaseOrders,
            remainedCapacity: allocation.remainedCapacity,
            weekKey: bucket.key,
            typeCode: calendarRow.typeCode,
            unit: calendarRow.unit,
            highlightedSegmentKey: options.selectedPo.selectedPoNumber.value,
            highlightedAnchorWeekKey: options.selectedPo.selectedBlock.value?.weekKey ?? null,
            dimUnhighlighted: true,
            compact: true,
            // Same order as the Scheduled row's cards, so a block distributed across types
            // reads as one vertical band instead of jumping position row to row.
            orderKeys: cellOrderKeys(row.original.lane.cells[bucket.key]),
            onSelectPo: (poNumber: string) => options.selectedPo.toggle(poNumber),
            onSelectAllocation: () => options.selectedPo.clear(),
            // blockKey groups a multi-type manual block; clicking any member toggles the whole block.
            // It is also the card's own key, so a reservation picked from a segment still gets ringed —
            // unlike a PO segment, which names a number without saying which week's card it came from.
            onSelectManual: (assignmentId: string, blockKey: string) => options.selectedPo.toggle(blockKey, { blockKey, weekKey: bucket.key }),
          }),
        ]);
      },
    })),
  ]);
}
